/**
 * 四个默认分类的**指定**配置（图标 + 配色），2026-10-04 用户给定。
 *
 * 之所以单独列一份而不是靠 `color` 自动派生：这三个色是手工挑的，
 * 并不是「同一个色兑淡/压暗」能算出来的（比如 娱乐 的背景色 #DCEEFF 蓝通道是满值 255，
 * 任何 alpha 混合都得不到），所以必须原样记下来。
 *
 * - `name` 是**纯名称**（不含图标前缀），匹配时先过 parseTagName 剥掉前缀
 * - `color` 是写库的那一个色（= 描边色），色点 / 图标色都从它派生
 * - `bg` / `border` / `textColor` 只用于渲染，不落库
 *
 * ⚠️ 只按名称精确匹配：用户改了名或自己建了同名分类之外的分类，就走 `derive` 自动派生。
 */
export const DEFAULT_CATEGORY_PRESETS = [
  { name: '生活', icon: 'house', color: '#E6B94A', bg: '#FFF3C7', border: '#E6B94A', textColor: '#8A6918' },
  { name: '娱乐', icon: 'gamepad', color: '#6FA8DC', bg: '#DCEEFF', border: '#6FA8DC', textColor: '#3D6F9E' },
  { name: '睡觉', icon: 'bed', color: '#82B87D', bg: '#DCF3DF', border: '#82B87D', textColor: '#4D794A' },
  { name: '学习', icon: 'book', color: '#E99A76', bg: '#FFE1D3', border: '#E99A76', textColor: '#9A5B42' }
]

/** 按名称（可以是带图标前缀的整串）找默认分类配置，找不到返回 null */
export function findDefaultPreset(name) {
  const text = parseTagName(name).text
  return DEFAULT_CATEGORY_PRESETS.find(p => p.name === text) || null
}

export const DAY_NAMES = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']

/**
 * 「我的分类」推荐颜色：24 种清新色调，按色相顺序排列（绿 → 青蓝 → 蓝紫 → 粉 → 橙黄 → 黄绿/沙/雾蓝），
 * 相邻色不重复，保证一眼能区分。
 * 用户也可以绕过这层选色，直接用取色器自定义（存进 color 字段的同样是 #RRGGBB）。
 */
export const TAG_COLORS = [
  // 绿
  '#b7e4c7', '#95d5b2', '#74c69d', '#a8e6cf',
  // 青 / 蓝
  '#a0e7e5', '#8ecae6', '#a7d8f0', '#b5d8f5',
  // 蓝 / 紫
  '#a9c9f2', '#b8a9e0', '#c9b6e4', '#d4c5f9',
  // 粉
  '#f2b8e0', '#f7c5d8', '#ffc9de', '#f8a5a5',
  // 橙 / 暖黄
  '#ffc4a3', '#ffd6a5', '#ffe0a3', '#f9e79f',
  // 黄绿 / 沙 / 雾蓝
  '#d9e8a7', '#e8d5b7', '#c7ceea', '#b0c4de'
]

export const DEFAULT_TAG_COLOR = '#a8e6cf'

/**
 * 时间块 / 图例标签的**统一文字色**（固定值）。
 *
 * 用户要求：图例里「分类」的文字色与时间块的文字色必须一致，
 * 且**不再随分类颜色变化**。分类的辨识交给底色 / 描边 / 图标，
 * 文字一律用这一档黑灰 —— 避免同一个页面里出现四五种深浅不一的「分类色文字」。
 *
 * ⚠️ 图例（`CategoryLegend.chipStyle`）与时间块（`TimeTable.sliceStyle`）都从这里取值，
 *    不要再各自写死字面量，否则两处会再次漂移。
 */
export const BLOCK_TEXT_COLOR = '#2d3748'

/**
 * 分类图标候选（Font Awesome 6 免费 solid 图标名）。
 * 分组展示 + 支持按名字搜索，避免 80 多个图标平铺成一堵墙。
 *
 * ⚠️ 这些名字都已在 node_modules/@fortawesome/free-solid-svg-icons 里核对过存在
 *    （beer-mug 在 FA6 被拆成 beer-mug-empty / beer-mug-full，故未收录）。
 *    渲染走 public/index.html 里的 FA 6.4.0 CDN CSS（`<i class="fas fa-xxx">`），
 *    不 import 图标对象，因此不增加打包体积。
 */
export const TAG_ICON_GROUPS = [
  {
    label: '学习',
    icons: ['book', 'book-open', 'graduation-cap', 'school', 'pen', 'pencil', 'clipboard', 'clipboard-list', 'flask', 'atom', 'calculator', 'glasses']
  },
  {
    label: '工作',
    icons: ['briefcase', 'laptop', 'desktop', 'chart-simple', 'chart-line', 'gear', 'wrench', 'folder', 'building', 'handshake', 'bullhorn', 'tag']
  },
  {
    label: '运动',
    icons: ['dumbbell', 'running', 'bicycle', 'person-walking', 'football', 'basketball', 'baseball', 'volleyball', 'medal', 'trophy']
  },
  {
    label: '饮食',
    icons: ['utensils', 'bowl-food', 'coffee', 'mug-hot', 'pizza-slice', 'burger', 'cake-candles', 'cookie', 'apple-whole', 'wine-glass', 'ice-cream']
  },
  {
    label: '生活',
    icons: ['house', 'bed', 'bath', 'broom', 'soap', 'shirt', 'socks', 'cart-shopping', 'truck', 'car', 'bus', 'train', 'plane']
  },
  {
    label: '娱乐',
    icons: ['film', 'music', 'gamepad', 'tv', 'headphones', 'guitar', 'camera', 'images', 'palette', 'puzzle-piece', 'dice', 'ghost']
  },
  {
    label: '健康',
    icons: ['heart', 'heart-pulse', 'stethoscope', 'pills', 'brain', 'tooth', 'spa']
  },
  {
    label: '其他',
    icons: ['sun', 'moon', 'cloud', 'star', 'leaf', 'seedling', 'tree', 'droplet', 'fire', 'bolt', 'snowflake', 'clock', 'calendar', 'bell', 'gift', 'rocket', 'globe', 'compass', 'anchor', 'key', 'crown', 'gem', 'flag']
  }
]

/** 名称字段的长度上限。= 用户可填字数(8) + 图标前缀 'fa:'(3) + 最长图标名(47) */
export const MAX_TAG_NAME_LEN = 58

/**
 * 图标前缀标记。图标不单独存字段，而是**拼进名称字段**里，靠这个前缀区分：
 *   'fa:book学习' -> 图标库图标(fa) + 名称「学习」
 *   '🍕吃饭'       -> 表情 + 名称「吃饭」
 *   '学习'         -> 没设图标
 *
 * 之所以用 'fa:' 这种带冒号的写法而不是单个特殊字符：
 *   - 冒号几乎不会出现在分类名里，冲突概率极低
 *   - 不用 \u0000 之类的控制字符 —— 它在 URL、日志、DB 客户端里都容易被截断或转义
 * 表情不需要前缀，因为「以非 ASCII 字符开头」本身就足以判定。
 */
const FA_ICON_PREFIX = 'fa:'

/**
 * 把「图标 + 名称」拼成最终写库的字符串。
 * @param {string} iconType 'fa' | 'emoji' | ''
 */
export function buildTagName(iconType, iconValue, text) {
  const t = (text || '').trim()
  if (!iconType || !iconValue) return t
  return iconType === 'fa' ? `${FA_ICON_PREFIX}${iconValue}${t}` : `${iconValue}${t}`
}

/**
 * 判断单个字符是不是 emoji（表情 / 象形符号）。
 *
 * ⚠️ **不能写成「非 ASCII 即 emoji」**：中文 / 日文 / 韩文 / 全角标点同样非 ASCII，
 * 于是「学习」会被拆成图标「学」+ 名称「习」—— 这正是「没设图标却把第一个字当图标」的原因。
 * 用 Unicode 属性 `\p{Extended_Pictographic}` 精确判定（🚀🍕❤★☕ 等都命中，汉字不命中）。
 * 老引擎不支持 Unicode 属性转义时，**正则构造**就会抛错（不是匹配时），
 * 所以必须 `new RegExp` + try/catch，失败后退到「非 ASCII 且不是宽字符文字」的区间兜底。
 */
let PICTORIAL_RE = null
try {
  PICTORIAL_RE = new RegExp('^\\p{Extended_Pictographic}', 'u')
} catch (e) {
  PICTORIAL_RE = null
}

/** 兜底用：这些区间的字符是「文字」不是表情（CJK 汉字/部首、假名、注音、谚文、全角） */
function isWideLetter(cp) {
  return (cp >= 0x1100 && cp <= 0x11ff) ||
    (cp >= 0x2e80 && cp <= 0x303f) ||
    (cp >= 0x3041 && cp <= 0x30ff) ||
    (cp >= 0x3400 && cp <= 0x4dbf) ||
    (cp >= 0x4e00 && cp <= 0x9fff) ||
    (cp >= 0xa000 && cp <= 0xa4cf) ||
    (cp >= 0xac00 && cp <= 0xd7a3) ||
    (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0xfe30 && cp <= 0xfe4f) ||
    (cp >= 0xff00 && cp <= 0xffef)
}

function isEmojiChar(ch) {
  if (!ch) return false
  const cp = ch.codePointAt(0)
  // 区域指示符 🇨🇳 这类国旗不属于 Extended_Pictographic，单独补上
  if (cp >= 0x1f1e6 && cp <= 0x1f1ff) return true
  if (PICTORIAL_RE) return PICTORIAL_RE.test(ch)
  return cp > 0x7f && !isWideLetter(cp)
}

/**
 * emoji 的「跟随片段」：本身不是表情，但属于同一个表情的一部分。
 * ZWJ(👨‍👩‍👧) / 变体选择符(❤️) / keycap(1️⃣) / 肤色 / 区域指示符(🇨🇳) / tag 序列(🏴󠁧󠁢󠁳󠁣󠁴󠁿)
 */
function isEmojiTail(cp) {
  return cp === 0x200d ||
    cp === 0xfe0f ||
    cp === 0x20e3 ||
    (cp >= 0x1f3fb && cp <= 0x1f3ff) ||
    (cp >= 0x1f1e6 && cp <= 0x1f1ff) ||
    (cp >= 0xe0020 && cp <= 0xe007f)
}

/**
 * 取出字符串**开头**的那个完整 emoji（可能由多个码点组成），不是 emoji 开头则返回空串。
 * 拿它当 iconValue 才能保证与 parseTagName 严格互逆（只取 1 个码点会把 🇨🇳 劈成半个）。
 */
export function leadingEmoji(str) {
  // 用 Array.from 按码点拆，否则代理对（如 '🚀'）会被 slice 劈成半个字符
  const chars = Array.from(str || '')
  if (!chars.length || !isEmojiChar(chars[0])) return ''
  let out = chars[0]
  for (let i = 1; i < chars.length; i++) {
    const cp = chars[i].codePointAt(0)
    if (!isEmojiChar(chars[i]) && !isEmojiTail(cp)) break
    out += chars[i]
  }
  return out
}

/**
 * 反向解析名称字段，拆成 { iconType, iconValue, text } 供展示用。
 * 与 buildTagName 严格互逆。
 *
 * 识别顺序：'fa:' 前缀 → 开头是 emoji → 都没有就是「无图标」（展示时回退成色点）。
 */
export function parseTagName(name) {
  const raw = (name || '').trim()
  if (!raw) return { iconType: '', iconValue: '', text: '' }

  if (raw.startsWith(FA_ICON_PREFIX)) {
    // 前缀之后、第一个非图标名字符之前是图标名；图标名只可能是 [a-z0-9-]
    const rest = raw.slice(FA_ICON_PREFIX.length)
    const matched = rest.match(/^[a-z0-9-]+/)
    if (matched) {
      return {
        iconType: 'fa',
        iconValue: matched[0],
        text: rest.slice(matched[0].length)
      }
    }
  }

  const emoji = leadingEmoji(raw)
  if (emoji) {
    return { iconType: 'emoji', iconValue: emoji, text: raw.slice(emoji.length) }
  }

  // 没有图标：iconType 为空串，渲染侧回退成该分类色的色点
  return { iconType: '', iconValue: '', text: raw }
}

/** 校验 #RRGGBB（允许省略 #），合法返回规范化的小写 hex，否则返回空串。 */
export function normalizeHexColor(input) {
  const s = (input || '').trim()
  const withHash = s.startsWith('#') ? s : '#' + s
  return /^#[0-9a-f]{6}$/i.test(withHash) ? withHash.toLowerCase() : ''
}

/**
 * 把 hex 颜色整体调暗/调亮（percent 为负变暗、为正变亮）。
 *
 * 推荐色都是「清新」的浅色系，直接拿去画 Font Awesome 图标会糊在白底上看不清，
 * 所以图标渲染前统一压暗一档；色块（dot）仍用原色，保留用户选的那个色。
 */
export function shadeHexColor(hex, percent) {
  const norm = normalizeHexColor(hex)
  if (!norm) return hex || ''
  const num = parseInt(norm.slice(1), 16)
  const amt = Math.round(2.55 * percent)
  const clamp = v => Math.max(0, Math.min(255, v))
  const r = clamp((num >> 16) + amt)
  const g = clamp(((num >> 8) & 0x00ff) + amt)
  const b = clamp((num & 0x0000ff) + amt)
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')
}

/**
 * hex → rgba 字符串。
 * 用来把分类色「兑淡」成标签底色：分类色本身是浅色系，直接铺满当背景会把文字压得看不清，
 * 降到低 alpha 叠在白底上就是一层很淡的同色底，既能区分分类又不影响可读性。
 */
export function hexToRgba(hex, alpha = 1) {
  const norm = normalizeHexColor(hex)
  if (!norm) return hex || ''
  const num = parseInt(norm.slice(1), 16)
  const r = (num >> 16) & 0x00ff
  const g = (num >> 8) & 0x00ff
  const b = num & 0x00ff
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/* ===== 分类配色的派生 =====
   图例标签、弹窗预览、表格时间块**共用这一套**，避免三处各算各的导致色差
   （曾出现「时间块用原色铺满、分类标签只兑淡 18%」，饱和度差一大截）。 */

function hexToRgb(hex) {
  const norm = normalizeHexColor(hex)
  if (!norm) return [0, 0, 0]
  const num = parseInt(norm.slice(1), 16)
  return [(num >> 16) & 0x00ff, (num >> 8) & 0x00ff, num & 0x00ff]
}

/** WCAG 相对亮度 */
function relLuminance(rgb) {
  const f = v => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * f(rgb[0]) + 0.7152 * f(rgb[1]) + 0.0722 * f(rgb[2])
}

function contrastRatio(a, b) {
  const la = relLuminance(a)
  const lb = relLuminance(b)
  const hi = Math.max(la, lb)
  const lo = Math.min(la, lb)
  return (hi + 0.05) / (lo + 0.05)
}

/** 底色 = 分类色按 alpha 兑淡后**叠在白卡上**的等效实色（用于算对比度） */
function tintOverWhite(hex, alpha) {
  return hexToRgb(hex).map(v => Math.round(255 - (255 - v) * alpha))
}

/** 将分类原色按比例混入白色，生成时间块稍浅、但仍保留辨识度的实色底 */
function lightenHexColor(hex, amount = 0.12) {
  const rgb = hexToRgb(hex).map(v => Math.round(v + (255 - v) * amount))
  return '#' + rgb.map(v => v.toString(16).padStart(2, '0')).join('')
}

/**
 * 分类色 → 一套渲染用色 `{ base, bg, blockBg, border, text }`。
 *
 * @param {string} color 写库的分类色（= 描边色那一档）
 * @param {string} [name] 分类名（可带图标前缀）；命中 DEFAULT_CATEGORY_PRESETS 就用指定配色
 *
 * 派生规则（没命中指定配色时）：
 * - `bg`     = 分类色兑淡到 alpha .18（浅色系直接铺满会把文字压得看不清）
 * - `border` = 分类色压暗一档（-25）
 * - `text`   = 一路压暗到与 bg 的对比度 ≥ 4.5（WCAG AA 正文下限）为止。
 *              不能写死一个值：色板里既有 #a8e6cf 这种很浅的、也有 #E6B94A 这种中间调，
 *              固定 -25 在浅色上只有 2.6:1，固定 -110 又会把中间调压成纯黑。
 */
export function categoryPalette(color, name) {
  const base = normalizeHexColor(color) || DEFAULT_TAG_COLOR
  const preset = name ? findDefaultPreset(name) : null
  if (preset) {
    return {
      base,
      bg: preset.bg,
      blockBg: lightenHexColor(base, 0.2),
      border: base,
      text: preset.textColor
    }
  }
  const alpha = 0.18
  const bgRgb = tintOverWhite(base, alpha)
  let text = shadeHexColor(base, -90)
  for (let p = 20; p <= 90; p += 5) {
    const candidate = shadeHexColor(base, -p)
    if (contrastRatio(hexToRgb(candidate), bgRgb) >= 4.5) {
      text = candidate
      break
    }
  }
  return {
    base,
    bg: hexToRgba(base, alpha),
    blockBg: lightenHexColor(base, 0.2),
    border: base,
    text
  }
}

export const DAY_MINUTES = 24 * 60

/**
 * 时间块的**本地稳定标识**（`evt.key`）。
 *
 * 为什么不能只用数组下标：一次「修改」可能先触发冲突处理，把其它记录删掉/改掉，
 * 数组长度一变，弹窗里早先记下来的 index 就指向**另一条**记录了 ——
 * 表现为「改了 A，界面上还是旧数据，还多出一个新块」，刷新后又回到旧数据。
 * 下标还会让 `events[index]` 取到 undefined，于是新块丢失后端 id，之后的删除/修改全都打空。
 *
 * key 只随「块的生命周期」走：新增时生成一次，修改/删除时原样保留，不受数组增删影响。
 * 后端 id 不能拿来当 key —— 离线兜底（接口失败 / 没选工作表）时块根本没有 id。
 */
let blockKeySeq = 0
export function nextBlockKey() {
  blockKeySeq += 1
  return `L${Date.now().toString(36)}-${blockKeySeq}`
}

export const SLOTS = (() => {
  const list = []
  for (let h = 0; h < 24; h++) {
    list.push(`${h}:00-${h + 1}:00`)
  }
  return list
})()

export function minutesToLabel(minutes) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}:${m.toString().padStart(2, '0')}`
}
